import { PaymentPlanInterval, type SaveFeeInput } from '@tyro/api';
import { RHFSelect, type RHFSelectProps } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import type { FieldValues } from 'react-hook-form';

export enum InstallmentInterval {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  BI_WEEKLY = 'BI_WEEKLY',
  MONTHLY = 'MONTHLY',
  BI_MONTHLY = 'BI_MONTHLY',
  QUARTERLY = 'QUARTERLY',
}

const installmentIntervalArray = Object.values(InstallmentInterval);

type PropsForInstallmentInterval = Pick<
  SaveFeeInput,
  'paymentPlanInterval' | 'paymentPlanIntervalCount'
>;

export function getInstallmentIntervalValueFromFormData({
  paymentPlanInterval,
  paymentPlanIntervalCount,
}: PropsForInstallmentInterval): InstallmentInterval {
  if (paymentPlanInterval === PaymentPlanInterval.Daily) {
    return InstallmentInterval.DAILY;
  }

  if (paymentPlanInterval === PaymentPlanInterval.Weekly) {
    if (paymentPlanIntervalCount === 1) return InstallmentInterval.WEEKLY;
    if (paymentPlanIntervalCount === 2) return InstallmentInterval.BI_WEEKLY;
  }

  if (paymentPlanInterval === PaymentPlanInterval.Monthly) {
    if (paymentPlanIntervalCount === 1) return InstallmentInterval.MONTHLY;
    if (paymentPlanIntervalCount === 2) return InstallmentInterval.BI_MONTHLY;
    if (paymentPlanIntervalCount === 3) return InstallmentInterval.QUARTERLY;
  }

  return InstallmentInterval.MONTHLY;
}
export function getFormDataFromInstallmentIntervalValue(
  value: InstallmentInterval,
): PropsForInstallmentInterval {
  const formValue: PropsForInstallmentInterval = {
    paymentPlanInterval: PaymentPlanInterval.Monthly,
    paymentPlanIntervalCount: 1,
  };

  if (value === InstallmentInterval.DAILY) {
    formValue.paymentPlanInterval = PaymentPlanInterval.Daily;
    formValue.paymentPlanIntervalCount = 1;
  } else if (
    [InstallmentInterval.WEEKLY, InstallmentInterval.BI_WEEKLY].includes(value)
  ) {
    formValue.paymentPlanInterval = PaymentPlanInterval.Weekly;
    formValue.paymentPlanIntervalCount =
      value === InstallmentInterval.WEEKLY ? 1 : 2;
  } else if (
    [
      InstallmentInterval.MONTHLY,
      InstallmentInterval.BI_MONTHLY,
      InstallmentInterval.QUARTERLY,
    ].includes(value)
  ) {
    formValue.paymentPlanInterval = PaymentPlanInterval.Monthly;
    if (value === InstallmentInterval.MONTHLY)
      formValue.paymentPlanIntervalCount = 1;
    if (value === InstallmentInterval.BI_MONTHLY)
      formValue.paymentPlanIntervalCount = 2;
    if (value === InstallmentInterval.QUARTERLY)
      formValue.paymentPlanIntervalCount = 3;
  }

  return formValue;
}

type RHFInstallmentIntervalFieldProps<TField extends FieldValues> = Omit<
  RHFSelectProps<TField, InstallmentInterval>,
  'options'
>;

export const RHFInstallmentIntervalField = <TField extends FieldValues>(
  props: RHFInstallmentIntervalFieldProps<TField>,
) => {
  const { t } = useTranslation(['fees']);

  return (
    <RHFSelect
      label={t('fees:installmentInterval')}
      options={installmentIntervalArray}
      getOptionLabel={(value) => t(`fees:installmentIntervalType.${value}`)}
      fullWidth
      {...props}
    />
  );
};
